import React, { FC } from 'react';
import { StyleSheet } from 'react-native';
import { Button, Typography, Icon } from '../../shared';
import { Popup } from '../../shared/popup';
import colors from '@packages/core/styles/colors';

interface WorksheetUploadWarningModalProps {
    /** Whether the modal is visible */
    visible: boolean;
    /** Callback when user confirms they want to leave */
    onConfirm: () => void;
    /** Callback when user cancels and wants to stay */
    onCancel: () => void;
    /** Optional custom warning message */
    message?: string;
}

/**
 * Modal component that warns users about leaving the page while worksheet uploads are in progress
 */
export const WorksheetUploadWarningModal: FC<WorksheetUploadWarningModalProps> = ({
    visible,
    onConfirm,
    onCancel,
    message = 'A file upload is currently in progress. Leaving this page may cancel the upload and cause your worksheet changes to be lost.\n\nAre you sure you want to leave?',
}) => {
    return (
        <Popup show={visible} onModalHide={onCancel} contentStyle={styles.modalContainer}>
            <div style={styles.iconContainer}>
                <Icon name="alert-triangle" size={48} color="grayFive" />
            </div>

            <div style={styles.messageContainer}>
                <Typography variant="h4" style={{ marginBottom: 12 }}>
                    Upload In Progress
                </Typography>
                <Typography variant="lead" style={{ lineHeight: 1.5 }}>
                    {message.split('\n\n').map((line, index, array) => (
                        <React.Fragment key={index}>
                            {line}
                            {index < array.length - 1 && (
                                <React.Fragment>
                                    <br />
                                    <br />
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    ))}
                </Typography>
            </div>

            <div style={styles.buttonContainer}>
                <Button variant="ghost-gray" onPress={onCancel} style={{ minWidth: 100 }}>
                    Stay on Page
                </Button>
                <Button variant="primary" onPress={onConfirm} style={{ minWidth: 100 }}>
                    Leave Page
                </Button>
            </div>
        </Popup>
    );
};

const styles = StyleSheet.create({
    modalContainer: {
        padding: 24,
        maxWidth: 500,
        backgroundColor: colors.white,
        borderRadius: 8,
        alignItems: 'center',
    },
    iconContainer: {
        marginBottom: 16,
        alignItems: 'center',
    },
    messageContainer: {
        marginBottom: 24,
        alignItems: 'center',
    },
    buttonContainer: {
        flexDirection: 'row',
        gap: 12,
        justifyContent: 'center',
    },
});
